import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { X, CheckCircle2, DollarSign } from 'lucide-react';

const GET_ASSESSMENT_QUESTIONS = gql`
  query AssessmentQuestions($keyPrefix: String) {
    assessmentQuestions(key_prefix: $keyPrefix) {
      id
      key
      text
      category
      seller_only
      scale_min_label
      scale_max_label
    }
  }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      succeedo_persona
    }
  }
`;

const COMPLETE_ASSESSMENT = gql`
  mutation CompleteAssessment($responses: [AssessmentResponseInput!]!, $wealthGoal: Float, $wealthCurrentAssets: Float) {
    complete_assessment(responses: $responses, wealth_goal: $wealthGoal, wealth_current_assets: $wealthCurrentAssets) {
      overall_score
      category_scores
      red_flags
      green_zones
      tier
      action_plan_item_count
      errors
    }
  }
`;

// Category config matching dashboard ActionPlanChecklist tokens
const CATEGORY_CONFIG = {
  financial:  { label: 'FINANCIAL',  color: 'chart-1' },
  human:      { label: 'HUMAN',      color: 'chart-2' },
  structural: { label: 'STRUCTURAL', color: 'chart-3' },
  customer:   { label: 'CUSTOMER',   color: 'chart-4' },
  wealth:     { label: 'WEALTH',     color: 'chart-5' },
};

function getCategoryStyle(category) {
  const config = CATEGORY_CONFIG[category] || {};
  const token = config.color || 'chart-1';
  return {
    color: `hsl(var(--${token}))`,
    borderColor: `hsl(var(--${token}) / 0.3)`,
  };
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function CommonSenseScoringWizard({ onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showTransition, setShowTransition] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWealthGapStep, setShowWealthGapStep] = useState(false);
  const [wealthGoal, setWealthGoal] = useState('');
  const [wealthCurrentAssets, setWealthCurrentAssets] = useState('');

  const { data: userData } = useQuery(GET_CURRENT_USER);
  const { data: questionData, loading: questionsLoading, error: questionsError } = useQuery(
    GET_ASSESSMENT_QUESTIONS,
    { variables: { keyPrefix: 'smb_' } }
  );

  const [completeAssessment] = useMutation(COMPLETE_ASSESSMENT);

  // Filter questions: if buyer, exclude seller-only questions
  const persona = userData?.getCurrentUser?.succeedo_persona;
  const allQuestions = questionData?.assessmentQuestions || [];
  const questions = persona === 'buyer'
    ? allQuestions.filter(q => !q.seller_only)
    : allQuestions;

  // Group by category for transition screens
  const categories = [...new Set(questions.map(q => q.category))];

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const isLastQuestion = currentIndex === totalQuestions - 1;

  // Determine if moving to a new category
  const currentCategory = currentQuestion?.category;
  const nextQuestion = currentIndex < totalQuestions - 1 ? questions[currentIndex + 1] : null;
  const nextCategory = nextQuestion?.category;
  const isCategoryChange = nextCategory && currentCategory !== nextCategory;

  const handleAnswer = useCallback((value) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  }, [currentQuestion]);

  const handleContinue = useCallback(() => {
    if (isLastQuestion) return;

    if (isCategoryChange) {
      setShowTransition(true);
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setShowTransition(false);
      }, 1500);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [isLastQuestion, isCategoryChange]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const responses = Object.entries(answers).map(([questionId, value]) => ({
        questionId,
        value,
      }));

      const { data } = await completeAssessment({
        variables: {
          responses,
          wealthGoal: wealthGoal ? parseFloat(wealthGoal) : null,
          wealthCurrentAssets: wealthCurrentAssets ? parseFloat(wealthCurrentAssets) : null,
        },
      });
      const result = data?.complete_assessment;

      if (result?.errors?.length > 0) {
        console.error('Assessment errors:', result.errors);
        setSubmitting(false);
        return;
      }

      if (onComplete) {
        onComplete(result);
      }
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      setSubmitting(false);
    }
  }, [answers, wealthGoal, wealthCurrentAssets, completeAssessment, onComplete]);

  const handleClose = useCallback(() => {
    if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
      window.location.href = '/succeedo/home';
    }
  }, []);

  if (questionsLoading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
        <div className="bg-white rounded-[16px] p-8 max-w-[680px] w-full shadow-2xl text-center">
          <div className="text-[15px] text-[#737373]">Loading assessment...</div>
        </div>
      </div>
    );
  }

  if (questionsError || questions.length === 0) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
        <div className="bg-white rounded-[16px] p-8 max-w-[680px] w-full shadow-2xl text-center">
          <div className="text-[15px] text-[#737373]">
            {questionsError ? 'Failed to load assessment questions.' : 'No assessment questions available.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--background))] to-[hsl(var(--muted))]/20 backdrop-blur-3xl" />

      {/* Wizard Panel */}
      <div className="relative bg-white rounded-[16px] p-8 max-w-[680px] w-full shadow-2xl">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {showWealthGapStep ? (
          /* Wealth Gap Capture Step */
          <div>
            {/* Progress Header — full */}
            <div className="mb-8">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3 tracking-wide">
                ALMOST DONE — WEALTH GAP
              </div>
              <div className="h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(var(--succeedo-orange))] transition-all duration-500" style={{ width: '100%' }} />
              </div>
            </div>

            {/* Category Pill */}
            <div className="mb-6">
              <span
                className="inline-block font-mono text-[11px] uppercase rounded-full px-3 py-1 border"
                style={getCategoryStyle('wealth')}
              >
                {CATEGORY_CONFIG.wealth.label}
              </span>
            </div>

            <h2 className="text-[17px] font-medium text-foreground leading-[28px] mb-2">
              What&rsquo;s your wealth goal?
            </h2>
            <p className="text-[14px] text-muted-foreground mb-8">
              Help us calculate your <span className="font-medium text-foreground">Wealth Gap</span> &mdash; the difference between where you want to be financially and where you are today (not including your business).
            </p>

            <div className="space-y-5 mb-8">
              {/* Wealth Goal */}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1.5">
                  Total wealth goal at retirement
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={wealthGoal}
                    onChange={(e) => setWealthGoal(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="2,000,000"
                    className="w-full bg-background border border-input rounded-[8px] pl-10 pr-4 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Current Net Worth (excl. business) */}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1.5">
                  Current net worth <span className="italic">(not including your business)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={wealthCurrentAssets}
                    onChange={(e) => setWealthCurrentAssets(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="500,000"
                    className="w-full bg-background border border-input rounded-[8px] pl-10 pr-4 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-12">
              <button
                onClick={() => setShowWealthGapStep(false)}
                className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
              >
                &larr; Back
              </button>

              <div className="flex items-center gap-4">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (!wealthGoal && !wealthCurrentAssets)}
                  className="bg-foreground text-background rounded-full px-6 py-3 font-medium transition-all disabled:bg-muted-foreground disabled:cursor-not-allowed hover:bg-foreground/90"
                >
                  {submitting ? 'Submitting...' : 'View Results \u2192'}
                </button>
              </div>
            </div>
          </div>
        ) : !showTransition ? (
          <>
            {/* Progress Header */}
            <div className="mb-8">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3 tracking-wide">
                QUESTION {currentIndex + 1} OF {totalQuestions} — {(CATEGORY_CONFIG[currentCategory] || {}).label || currentCategory?.toUpperCase()}
              </div>
              <div className="h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--succeedo-orange))] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Category Pill */}
            <div className="mb-6">
              <span
                className="inline-block font-mono text-[11px] uppercase rounded-full px-3 py-1 border"
                style={getCategoryStyle(currentCategory)}
              >
                {(CATEGORY_CONFIG[currentCategory] || {}).label || currentCategory}
              </span>
            </div>

            {/* Question */}
            <h2 className="text-[17px] font-medium text-foreground leading-[28px] mb-8">
              {currentQuestion.text}
            </h2>

            {/* Scale */}
            <div className="mb-6">
              <div className="flex justify-between gap-3 mb-3">
                {[1, 2, 3, 4, 5, 6].map((value) => (
                  <button
                    key={value}
                    onClick={() => handleAnswer(value)}
                    className={`h-10 w-10 rounded-full border font-mono text-[13px] font-medium transition-all ${
                      currentAnswer === value
                        ? 'bg-[hsl(var(--succeedo-orange))] text-white border-[hsl(var(--succeedo-orange))] shadow-[0_0_0_4px_hsl(var(--succeedo-orange)/0.2)]'
                        : 'bg-background border-[hsl(var(--border))] text-muted-foreground hover:border-[hsl(var(--muted-foreground))]'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex justify-between">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">
                  {currentQuestion.scale_min_label || 'LOW'}
                </span>
                <span className="font-mono text-[10px] uppercase text-muted-foreground">
                  {currentQuestion.scale_max_label || 'HIGH'}
                </span>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-12">
              <button
                onClick={handleBack}
                disabled={currentIndex === 0}
                className="text-[14px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                &larr; Back
              </button>

              {isLastQuestion ? (
                <button
                  onClick={() => setShowWealthGapStep(true)}
                  disabled={!currentAnswer}
                  className="bg-foreground text-background rounded-full px-6 py-3 font-medium transition-all disabled:bg-muted-foreground disabled:cursor-not-allowed hover:bg-foreground/90"
                >
                  Continue &rarr;
                </button>
              ) : (
                <button
                  onClick={handleContinue}
                  disabled={!currentAnswer}
                  className="bg-foreground text-background rounded-full px-6 py-3 font-medium transition-all disabled:bg-muted-foreground disabled:cursor-not-allowed hover:bg-foreground/90"
                >
                  Continue &rarr;
                </button>
              )}
            </div>
          </>
        ) : (
          /* Category Transition Screen */
          <div className="py-16 text-center">
            <CheckCircle2 className="w-8 h-8 text-[hsl(var(--succeedo-orange))] mx-auto mb-6" />
            <h2 className="text-[28px] font-light text-foreground mb-3">
              {capitalize(currentCategory)} Complete
            </h2>
            {nextCategory && (
              <p className="text-[15px] text-muted-foreground mb-8">
                Next: {capitalize(nextCategory)}
              </p>
            )}
            <div className="inline-block bg-foreground text-background rounded-full px-6 py-3 font-medium">
              Continue &rarr;
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
